import { ChangeEvent, JSX, useMemo } from 'react';
import * as vp from 'vopay';
import moment from 'moment';
import { useClientData } from '../../../providers/ClientData.provider';
import { FormData } from './types';
import { useTranslation } from 'react-i18next';

export type WithdrawAccountRows = {
  label: string;
  id: vp.WithdrawAccountId;
  require: boolean;
  preset: keyof vp.TextFieldPresets | undefined;
  type: 'input' | 'select' | 'toggle' | 'singleDate';
  isReadOnly?: boolean;
  placeholder: string;
  visible: (ctx: FormData) => boolean;
  maxLength?: number;
  disableFiltering?: (ctx: FormData) => boolean;
  searchCallback?: (term: string) => void;
  onChangeCallBack?: (event: ChangeEvent<HTMLInputElement>, ctx: FormData) => void;
  SelectLabel?: (ctx: FormData) => string | undefined;
  getSelectOptions: (ctx: FormData) => { [key: string]: string | JSX.Element };
};
type BuildWithdrawAccountProps = {
  moreInfoNeeded: boolean;
  clientAccount?: vp.ClientAccount;
};
export const buildWithdrawAccountRows = ({
  moreInfoNeeded,
  clientAccount,
}: BuildWithdrawAccountProps): WithdrawAccountRows[] => {
  const {
    response: { USDEFTSendEnabled, ClientAccountsEnabled, TransactionLabelOverrideEnabled, Country },
  } = useClientData();

  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.WithdrawMyAccount.Form' });
  const { t: tGlobal } = useTranslation('translations');

  const clientAccountOptions = useMemo(() => {
    if (clientAccount) {
      return {
        [clientAccount.ClientAccountID]: clientAccount.ClientName,
      };
    }

    return {};
  }, [clientAccount]);

  const CurrencyOptions = {
    ...vp.CURRENCY_SELECT_OPTIONS,
  };

  const CADOnly: { [key: string]: string } = {
    CAD: 'CAD',
  };

  const FrequencyOptions = {
    ...vp.FREQUENCY_OPTIONS,
  };

  const PaymentOptions: { [key: string]: string | JSX.Element } = {
    'Bank Account': tGlobal('Global.PaymentMethods.bankAccount'),
    'VoPay Instant': 'VoPay Instant',
    'Interac e-Transfer': <vp.InteracWordmark />,
  };

  const BankAccountOption: { [key: string]: string } = {
    'Bank Account': tGlobal('Global.BankAccount.bankAccount'),
  };

  const ScheduleTypeOptions: { [key: string]: string } = {
    single: tGlobal('Global.ScheduleType.single'),
    recurring: tGlobal('Global.ScheduleType.recurring'),
  };

  const EndingOptions: { [key: string]: string } = {
    'End Date': tGlobal('Global.EndingOptions.endDate'),
    Payments: tGlobal('Global.EndingOptions.payments'),
  };

  const SemiMonthlyDateOptions = {
    SemiMonthFirstAndFifteenth: tGlobal('Global.SemiMonthlyDateOptions.firstAndFifteenth'),
    SemiMonthFifteenthAndLast: tGlobal('Global.SemiMonthlyDateOptions.fifteenthAndLast'),
  };

  return [
    {
      label: t('Account.label'),
      id: vp.WithdrawAccountId.Account,
      require: true,
      preset: 'text',
      type: 'input',
      placeholder: t('Account.placeholder'),
      visible: () => ClientAccountsEnabled === '0',
      isReadOnly: true,
      getSelectOptions: () => ({}),
    },
    {
      label: t('ClientAccount.label'),
      id: vp.WithdrawAccountId.ClientAccount,
      require: true,
      preset: 'text',
      type: 'select',
      isReadOnly: true,
      placeholder: t('ClientAccount.placeholder'),
      visible: () => ClientAccountsEnabled === '1',
      getSelectOptions: () => clientAccountOptions,
    },
    {
      label: t('currency'),
      id: vp.WithdrawAccountId.Currency,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: () => true,
      getSelectOptions: () =>
        Country === 'US' ? vp.US_CURRENCY_ONLY : USDEFTSendEnabled === '0' ? CADOnly : CurrencyOptions,
    },
    {
      label: t('PaymentMethod.label'),
      id: vp.WithdrawAccountId.PaymentMethod,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: t('PaymentMethod.placeholder'),
      visible: () => ClientAccountsEnabled === '1',
      isReadOnly: true,
      getSelectOptions: (ctx) => (ctx.Currency === 'CAD' ? PaymentOptions : BankAccountOption),
    },
    {
      label: t('Amount.label'),
      id: vp.WithdrawAccountId.Amount,
      require: true,
      preset: 'currency',
      type: 'input',
      placeholder: t('Amount.placeholder'),
      visible: () => true,
      getSelectOptions: () => ({}),
    },
    {
      label: t('scheduleType'),
      id: vp.WithdrawAccountId.ScheduleType,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx) =>
        ctx.PaymentMethod === 'Bank Account' &&
        ((Country === 'CA' && ctx.Currency === 'CAD') || (Country === 'US' && ctx.Currency === 'USD')),
      getSelectOptions: () => ScheduleTypeOptions,
    },
    {
      label: t('TakeFromAccountOn.label'),
      id: vp.WithdrawAccountId.TakeFromAccountOn,
      require: true,
      preset: 'date',
      type: 'singleDate',
      placeholder: t('TakeFromAccountOn.placeholder'),
      visible: (ctx) =>
        ctx.PaymentMethod === 'Bank Account' &&
        ctx.ScheduleType === 'single' &&
        ((Country === 'CA' && ctx.Currency === 'CAD') || (Country === 'US' && ctx.Currency === 'USD')),
      getSelectOptions: () => ({}),
    },
    {
      label: t('StartDate.label'),
      id: vp.WithdrawAccountId.StartDate,
      require: true,
      preset: 'date',
      type: 'singleDate',
      placeholder: t('StartDate.placeholder'),
      visible: (ctx) =>
        ctx.PaymentMethod === 'Bank Account' &&
        ctx.ScheduleType === 'recurring' &&
        ((Country === 'CA' && ctx.Currency === 'CAD') || (Country === 'US' && ctx.Currency === 'USD')),
      getSelectOptions: () => ({}),
    },
    {
      label: t('frequency'),
      id: vp.WithdrawAccountId.Frequency,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx) =>
        ctx.PaymentMethod === 'Bank Account' &&
        ctx.ScheduleType === 'recurring' &&
        ((Country === 'CA' && ctx.Currency === 'CAD') || (Country === 'US' && ctx.Currency === 'USD')),
      getSelectOptions: () => FrequencyOptions,
    },
    {
      label: t('semiMonthlyDates'),
      id: vp.WithdrawAccountId.SemiMonthlyDates,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx) =>
        ctx.PaymentMethod === 'Bank Account' &&
        ctx.ScheduleType === 'recurring' &&
        ctx.Frequency === 'semi-monthly' &&
        ((Country === 'CA' && ctx.Currency === 'CAD') || (Country === 'US' && ctx.Currency === 'USD')),
      getSelectOptions: () => SemiMonthlyDateOptions,
    },
    {
      label: t('ending'),
      id: vp.WithdrawAccountId.Ending,
      require: false,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx) =>
        ctx.PaymentMethod === 'Bank Account' &&
        ctx.ScheduleType === 'recurring' &&
        ((Country === 'CA' && ctx.Currency === 'CAD') || (Country === 'US' && ctx.Currency === 'USD')),
      getSelectOptions: () => EndingOptions,
    },
    {
      label: t('EndDate.label'),
      id: vp.WithdrawAccountId.EndDate,
      require: true,
      preset: 'date',
      type: 'singleDate',
      placeholder: t('EndDate.placeholder'),
      visible: (ctx) =>
        ctx.PaymentMethod === 'Bank Account' &&
        ctx.ScheduleType === 'recurring' &&
        ctx.Ending === 'End Date' &&
        ((Country === 'CA' && ctx.Currency === 'CAD') || (Country === 'US' && ctx.Currency === 'USD')),
      getSelectOptions: () => ({}),
    },
    {
      label: t('endingAfterPayments'),
      id: vp.WithdrawAccountId.EndingAfterPayments,
      require: true,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx) =>
        ctx.Ending === 'Payments' &&
        ((Country === 'CA' && ctx.Currency === 'CAD') || (Country === 'US' && ctx.Currency === 'USD')),
      getSelectOptions: () => ({}),
    },
    {
      label: t('Description.label'),
      id: vp.WithdrawAccountId.Description,
      require: true,
      preset: 'text',
      type: 'input',
      placeholder: t('Description.placeholder'),
      visible: (ctx) =>
        ctx.PaymentMethod === 'Bank Account' &&
        (ctx.ScheduleType === 'recurring' ||
          (ctx.TakeFromAccountOn !== '' &&
            !vp.IsTodayDate(moment(Number(ctx.TakeFromAccountOn)).format('YYYY-MM-DD')))) &&
        ((Country === 'CA' && ctx.Currency === 'CAD') || (Country === 'US' && ctx.Currency === 'USD')),
      getSelectOptions: () => ({}),
    },
    {
      label: t('messageForRecipient'),
      id: vp.WithdrawAccountId.MessageForRecipient,
      require: false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx) => ctx.PaymentMethod === 'Interac e-Transfer',
      getSelectOptions: () => ({}),
    },
    {
      label: t('transactionLabel'),
      id: vp.WithdrawAccountId.TransactionLabel,
      require: false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      maxLength: 30,
      visible: (ctx) =>
        TransactionLabelOverrideEnabled === '1' && ctx.PaymentMethod === 'Bank Account' && moreInfoNeeded,
      getSelectOptions: () => ({}),
    },
    {
      label: t('notes'),
      id: vp.WithdrawAccountId.Notes,
      require: false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx) => ctx.PaymentMethod !== 'Credit Card' && moreInfoNeeded,
      getSelectOptions: () => ({}),
    },
  ];
};
